import IconFilter from "@mui/icons-material/FilterAlt";
import { Stack } from "@mui/material";
import { useTranslation } from "react-i18next";

import { GqlPlannerV2AreaStatus, GqlProjectStatus } from "~/__generated__/graphql/types";
import { MapPillPanel } from "~/modules/map/components/MapPillPanel";
import { VisitStatusFilter } from "~/modules/plannerV2/components/VisitStatusFilter";
import ClientSelect from "~/shared/components/ClientSelect";
import FieldworkerSelect from "~/shared/components/FieldworkerSelect";
import PlannerAreaSelect from "~/shared/components/PlannerAreaSelect";
import ProjectSelect from "~/shared/components/ProjectSelect";
import ResearchTypeSelect from "~/shared/components/ResearchTypeSelect";

import { usePlannerV2AreaFilters } from "../../hooks/usePlannerV2AreaFilters";
import { MaxRequiredFieldworkersFilter } from "./MaxRequiredFieldworkersFilter";

export function PlannerV2AreaFilters({ maxRequiredFieldworkers }: { maxRequiredFieldworkers: number }) {
    const { t } = useTranslation("plannerV2");
    const {
        filterValues,
        setFilterValues,
        toggleVisitRiskStatus,
        setClientIds,
        setProjectIds,
        setFieldworkerIds,
        hasAnyFilters,
    } = usePlannerV2AreaFilters();

    return (
        <MapPillPanel icon={<IconFilter />} title={t("areaFilters.title")} hasDot={hasAnyFilters}>
            <Stack rowGap={2} margin={2}>
                <VisitStatusFilter filterStatuses={filterValues.plannerStatusBucket} onChange={toggleVisitRiskStatus} />
                <ClientSelect label={t("areaFilters.clientFilter")} value={filterValues.clientIds} onChange={setClientIds} />
                <ProjectSelect
                    multiple
                    label={t("areaFilters.projectFilter")}
                    value={filterValues.projectIds}
                    clientIds={filterValues.clientIds}
                    statuses={[GqlProjectStatus.Confirmed, GqlProjectStatus.Open]}
                    onChange={setProjectIds}
                />
                <PlannerAreaSelect
                    label={t("areaFilters.plannerAreaFilter")}
                    value={filterValues.plannerAreaIds}
                    clientIds={filterValues.clientIds}
                    projectIds={filterValues.projectIds}
                    statuses={[GqlPlannerV2AreaStatus.Confirmed]}
                    onChange={value => setFilterValues({ plannerAreaIds: value })}
                />
                <FieldworkerSelect
                    label={t("areaFilters.fieldworkerFilter")}
                    value={filterValues.plannedFieldworkerIds}
                    onChange={setFieldworkerIds}
                />
                <ResearchTypeSelect
                    label={t("areaFilters.researchTypeFilter")}
                    value={filterValues.researchTypeIds}
                    projectIds={filterValues.projectIds}
                    onChange={value => setFilterValues({ researchTypeIds: value })}
                />
                <MaxRequiredFieldworkersFilter
                    value={filterValues.maxRequiredFieldworkers}
                    max={maxRequiredFieldworkers}
                    onChange={value => setFilterValues({ maxRequiredFieldworkers: value })}
                />
            </Stack>
        </MapPillPanel>
    );
}
